import { BarChart3, BookOpen, CalendarCheck, ClipboardCheck, Download, Mail, ShieldAlert, Clock, Globe, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, tokens } from '../lib/api';
import { clock } from '../lib/format';
import type { Attendance, ChildReport, RecordedAttendance, ReportItems, ReportMetric } from '../lib/types';
import { useParent } from './ParentContext';

type Preset = '7' | '30' | '90' | 'year' | 'custom';
const PRESETS: { id: Preset; label: string }[] = [
  { id: '7', label: '7 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
  { id: 'year', label: 'School year' },
  { id: 'custom', label: 'Custom' },
];

type ReportType = 'recorded' | 'activity' | 'attendance' | 'sites';
const REPORT_TYPES: { id: ReportType; label: string; path: string }[] = [
  { id: 'recorded', label: 'Attendance report (hours you recorded)', path: 'attendance-recorded.csv' },
  { id: 'activity', label: 'Activity report', path: 'report.csv' },
  { id: 'attendance', label: 'System estimated attendance report', path: 'attendance.csv' },
  { id: 'sites', label: 'Time per learning site', path: 'sites.csv' },
];

/** 'YYYY-MM-DD' as a short local date, e.g. "Aug 25, 2026". */
const fmtDay = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** Local YYYY-MM-DD, for the custom date inputs' defaults. */
const isoDay = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Hours as "5 h" / "4.5 h". */
const fmtHours = (h: number) => `${Math.round(h * 100) / 100} h`;

function hm(mins: number): string {
  if (!mins) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

function Stat({
  icon,
  value,
  label,
  tone,
  onClick,
  active,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  tone?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const className = `report-stat ${tone ?? ''} ${onClick ? 'clickable' : ''} ${active ? 'active' : ''}`.trim();
  const inner = (
    <>
      <div className="report-stat-icon">{icon}</div>
      <div className="report-stat-value">{value}</div>
      <div className="report-stat-label">{label}</div>
    </>
  );
  if (!onClick) return <div className={className}>{inner}</div>;
  return (
    <button type="button" className={className} onClick={onClick} aria-pressed={active} aria-label={`${label}: ${value}. Show details`}>
      {inner}
    </button>
  );
}

const METRIC_TITLES: Record<ReportMetric, string> = {
  active: 'Time on each learning site',
  visits: 'Learning-site visits',
  blocked: 'Blocked attempts',
  offtask: 'Off-task alerts',
};
const METRIC_EMPTY: Record<ReportMetric, string> = {
  active: 'No learning-site time was logged in this range.',
  visits: 'No learning sites were visited in this range.',
  blocked: 'Nothing was blocked in this range — nice.',
  offtask: 'No off-task alerts in this range.',
};

export function Reports() {
  const { children, parent, toast } = useParent();
  // Sites added to the learning sites from this report (#861), shown as learning sites straight away.
  const [learned, setLearned] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState('');
  const [childId, setChildId] = useState(children[0]?.id ?? '');
  const [preset, setPreset] = useState<Preset>('7');
  const [customFrom, setCustomFrom] = useState(() => isoDay(Date.now() - 7 * 86_400_000));
  const [customTo, setCustomTo] = useState(() => isoDay(Date.now()));
  const [reportType, setReportType] = useState<ReportType>('recorded');
  const [report, setReport] = useState<ChildReport | null>(null);
  const [attendance, setAttendance] = useState<Attendance | null>(null);
  const [recorded, setRecorded] = useState<RecordedAttendance | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailTo, setEmailTo] = useState(parent.email);
  const [sending, setSending] = useState(false);
  // Drill-down: clicking a headline stat shows the items it counted (#150). 'days' scrolls to the By-day table.
  const [drill, setDrill] = useState<ReportMetric | null>(null);
  const [drillData, setDrillData] = useState<ReportItems | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const byDayRef = useRef<HTMLElement>(null);

  // Keep a valid child selected as the family loads/changes.
  useEffect(() => {
    if (!children.some((c) => c.id === childId)) setChildId(children[0]?.id ?? '');
  }, [children, childId]);

  // The selected child's school year (from Settings → School Settings), for the "School year" range (#183).
  const schoolYear = children.find((c) => c.id === childId)?.schoolYear ?? null;
  // Switching to a child without a school year falls back to the default range.
  useEffect(() => {
    if (preset === 'year' && !schoolYear) setPreset('7');
  }, [preset, schoolYear]);

  const range = useMemo(() => {
    if (preset === 'year' && schoolYear) {
      // From the first day of the school year up to today (or its last day, if it's over) — future days
      // haven't happened, so they mustn't show up as absences.
      const from = new Date(`${schoolYear.start}T00:00:00`).getTime();
      const end = new Date(`${schoolYear.end}T23:59:59.999`).getTime();
      return { from, to: Math.max(from, Math.min(end, Date.now())) };
    }
    if (preset === 'custom') {
      const from = new Date(`${customFrom}T00:00:00`).getTime();
      const to = new Date(`${customTo}T23:59:59.999`).getTime();
      return { from: Number.isNaN(from) ? Date.now() - 7 * 86_400_000 : from, to: Number.isNaN(to) ? Date.now() : to };
    }
    const to = Date.now();
    return { from: to - (Number(preset) || 7) * 86_400_000, to };
  }, [preset, customFrom, customTo, schoolYear]);

  const load = useCallback(() => {
    if (!childId) {
      setReport(null);
      return;
    }
    setLoading(true);
    const qs = `from=${range.from}&to=${range.to}`;
    Promise.all([
      api.get<ChildReport>(`/api/children/${childId}/report?${qs}`),
      api.get<Attendance>(`/api/children/${childId}/attendance?${qs}`),
      api.get<RecordedAttendance>(`/api/children/${childId}/attendance/recorded?${qs}`),
    ])
      .then(([r, a, rec]) => {
        setReport(r);
        setAttendance(a);
        setRecorded(rec);
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }))
      .finally(() => setLoading(false));
  }, [childId, range, toast]);
  useEffect(load, [load]);

  // Close any open drill-down when the child or date range changes (its items no longer match).
  useEffect(() => setDrill(null), [childId, range.from, range.to]);

  // Fetch the items behind the selected stat.
  useEffect(() => {
    if (!drill || !childId) {
      setDrillData(null);
      return;
    }
    let cancelled = false;
    setDrillLoading(true);
    setDrillData(null);
    api
      .get<ReportItems>(`/api/children/${childId}/report/items?metric=${drill}&from=${range.from}&to=${range.to}`)
      .then((d) => !cancelled && setDrillData(d))
      .catch((e) => !cancelled && toast({ text: (e as Error).message, tone: 'alert' }))
      .finally(() => !cancelled && setDrillLoading(false));
    return () => {
      cancelled = true;
    };
  }, [drill, childId, range.from, range.to, toast]);

  const toggleDrill = (metric: ReportMetric) => setDrill((cur) => (cur === metric ? null : metric));
  const showByDay = () => byDayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  async function exportCsv() {
    try {
      const path = REPORT_TYPES.find((t) => t.id === reportType)!.path;
      const res = await fetch(`/api/children/${childId}/${path}?from=${range.from}&to=${range.to}`, {
        headers: { Authorization: `Bearer ${tokens.parent()}` },
      });
      if (!res.ok) throw new Error('Could not export the report.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers.get('content-disposition')?.match(/filename="(.+?)"/)?.[1]) || 'classguardian-report.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  async function emailReport() {
    if (!childId) return;
    setSending(true);
    try {
      const { to } = await api.post<{ ok: true; to: string }>(`/api/children/${childId}/report/email?from=${range.from}&to=${range.to}`, {
        to: emailTo.trim() || undefined,
      });
      toast({ text: `Activity report emailed to ${to}.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setSending(false);
    }
  }

  if (!children.length) {
    return (
      <div className="page narrow">
        <h1>Reports</h1>
        <p className="muted">Add a child to see their activity report.</p>
      </div>
    );
  }

  const maxSiteMinutes = Math.max(1, ...(report?.topSites.map((s) => s.minutes) ?? [1]));
  const addLearning = async (host: string) => {
    setAdding(host);
    try {
      await api.post(`/api/children/${childId}/rules`, { kind: 'learn', pattern: host });
      setLearned((l) => new Set(l).add(`${childId}|${host}`));
      toast({ text: `Added ${host} to ${report?.childName ?? 'your child'}’s learning sites`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setAdding('');
    }
  };

  return (
    <div className="page narrow">
      <h1>Reports</h1>
      <div className="report-controls card">
        <label className="grow">
          Child
          <select value={childId} onChange={(e) => setChildId(e.target.value)}>
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <div className="segmented" role="radiogroup" aria-label="Date range">
          {PRESETS.map((p) => {
            const off = p.id === 'year' && !schoolYear;
            return (
              <button
                key={p.id}
                role="radio"
                aria-checked={preset === p.id}
                className={preset === p.id ? 'active' : ''}
                onClick={() => setPreset(p.id)}
                disabled={off}
                title={off ? 'Set the school year in Settings → School Settings to use this' : undefined}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        {preset === 'year' && schoolYear && (
          <p className="muted small">
            School year: {fmtDay(schoolYear.start)} – {fmtDay(schoolYear.end)}
            {new Date(`${schoolYear.end}T23:59:59.999`).getTime() > Date.now() && ' (showing up to today)'}
          </p>
        )}
        {!schoolYear && (
          <p className="muted small">
            Want a <strong>School year</strong> report? <Link to="/settings#school-settings">Set the school year</Link> in Settings first.
          </p>
        )}
        {preset === 'custom' && (
          <div className="field-row custom-range">
            <label>
              From
              <input type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
            </label>
          </div>
        )}
        <div className="field-row export-row">
          <label className="grow">
            Download
            <select value={reportType} onChange={(e) => setReportType(e.target.value as ReportType)}>
              {REPORT_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <button className="btn" onClick={exportCsv} disabled={!report}>
            <Download size={15} /> Export CSV
          </button>
        </div>
        {parent.emailAvailable && (
          <div className="field-row export-row">
            <label className="grow">
              Email the activity report to
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder={parent.email}
                autoComplete="email"
              />
            </label>
            <button className="btn" onClick={emailReport} disabled={!report || sending || !emailTo.trim()}>
              <Mail size={15} /> {sending ? 'Sending…' : 'Email report'}
            </button>
          </div>
        )}
      </div>

      {loading && !report ? (
        <p className="muted">Loading…</p>
      ) : report ? (
        <>
          {(report.childAge || report.childGrade) && (
            <p className="muted small report-profile">
              {report.childName}
              {report.childAge ? ` · Age ${report.childAge}` : ''}
              {report.childGrade ? ` · Grade ${report.childGrade}` : ''}
            </p>
          )}
          <div className="report-stats">
            <Stat icon={<Clock size={18} />} value={hm(report.activeMinutes)} label="Active on learning sites" onClick={() => toggleDrill('active')} active={drill === 'active'} />
            <Stat icon={<Globe size={18} />} value={report.siteVisits} label="Learning-site visits" onClick={() => toggleDrill('visits')} active={drill === 'visits'} />
            <Stat icon={<ShieldAlert size={18} />} value={report.blocked} label="Blocked attempts" tone={report.blocked ? 'warn' : ''} onClick={() => toggleDrill('blocked')} active={drill === 'blocked'} />
            <Stat icon={<BarChart3 size={18} />} value={report.offTask} label="Off-task alerts" tone={report.offTask ? 'warn' : ''} onClick={() => toggleDrill('offtask')} active={drill === 'offtask'} />
            <Stat icon={<Clock size={18} />} value={report.daysActive} label="Days active" onClick={showByDay} />
          </div>

          {drill && (
            <section className="card stack report-drill">
              <div className="report-drill-head">
                <h2 className="with-icon">
                  <BarChart3 size={18} /> {METRIC_TITLES[drill]}
                  {drillData && <span className="muted small report-drill-count">{drillData.count}</span>}
                </h2>
                <button className="icon-btn" onClick={() => setDrill(null)} aria-label="Close details">
                  <X size={18} />
                </button>
              </div>
              {drillLoading ? (
                <p className="muted small">Loading…</p>
              ) : !drillData || !drillData.items.length ? (
                <p className="muted small">{METRIC_EMPTY[drill]}</p>
              ) : (
                <>
                  <ul className="report-items">
                    {drillData.items.map((it, i) => (
                      <li key={i}>
                        <span className="report-item-primary">{it.primary}</span>
                        <span className="muted small report-item-secondary">{it.secondary}</span>
                      </li>
                    ))}
                  </ul>
                  {drillData.count > drillData.items.length && (
                    <p className="muted small center">Showing the most recent {drillData.items.length} of {drillData.count}. Narrow the date range to see more.</p>
                  )}
                </>
              )}
            </section>
          )}

          {recorded && (
            <section className="card stack">
              <h2 className="with-icon">
                <ClipboardCheck size={18} /> Attendance
              </h2>
              {recorded.days.length ? (
                <>
                  <div className="report-stats attendance-stats">
                    <Stat icon={<CalendarCheck size={18} />} value={recorded.daysAttended} label="Days attended" />
                    <Stat icon={<Clock size={18} />} value={fmtHours(recorded.totalHours)} label="Total hours" />
                    <Stat icon={<BarChart3 size={18} />} value={fmtHours(recorded.averageHours)} label="Average per day" />
                  </div>
                  <ul className="attendance-list">
                    {[...recorded.days].reverse().map((d) => (
                      <li key={d.date} className={d.hours > 0 ? 'present' : 'absent'}>
                        <span className={`attendance-badge ${d.hours > 0 ? 'present' : 'absent'}`}>{d.hours > 0 ? 'Attended' : 'Absent'}</span>
                        <span className="grow">
                          {d.weekday} {fmtDay(d.date)}
                        </span>
                        <span className="attendance-hours">{fmtHours(d.hours)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="muted small">From the hours you recorded on each child's Attendance tab.</p>
                </>
              ) : (
                <p className="muted small">
                  No hours recorded in this range. Record each day's hours on{' '}
                  <Link to={`/kids/${childId}/attendance`}>{report.childName}'s Attendance tab</Link>.
                </p>
              )}
            </section>
          )}

          <section className="card stack">
            <h2 className="with-icon">
              <Globe size={18} /> Top sites
            </h2>
            {report.topSites.length ? (
              <ul className="report-sites report-top-sites">
                {report.topSites.map((s) => {
                  const status = learned.has(`${childId}|${s.host}`) ? 'learning' : s.status;
                  return (
                    <li key={s.host}>
                      <span className="report-site-host">{s.host}</span>
                      <span className="report-bar" style={{ width: `${(s.minutes / maxSiteMinutes) * 100}%` }} />
                      <span className="report-site-time">{hm(s.minutes)}</span>
                      {status === 'other' ? (
                        <button type="button" className="btn small report-site-add" disabled={adding === s.host} onClick={() => addLearning(s.host)}>
                          <Plus size={14} /> Add to learning sites
                        </button>
                      ) : (
                        <span className={`report-site-chip ${status}`}>{status === 'learning' ? 'Learning site' : 'Blocked'}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted small">No websites were logged in this range.</p>
            )}
          </section>

          {!!report.byApp?.length && (
            <section className="card stack">
              <h2 className="with-icon">
                <BookOpen size={18} /> Approved apps
              </h2>
              <p className="muted small">Time in the apps you approved. It counts toward active time and the attendance estimate.</p>
              <ul className="report-sites">
                {report.byApp.map((a) => (
                  <li key={a.name}>
                    <span className="report-site-host">{a.name}</span>
                    <span className="report-bar" style={{ width: `${(a.minutes / Math.max(...report.byApp!.map((x) => x.minutes))) * 100}%` }} />
                    <span className="muted small">{a.minutes} min</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="card stack" ref={byDayRef}>
            <h2 className="with-icon">
              <BarChart3 size={18} /> By day
            </h2>
            {report.byDay.length ? (
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Active</th>
                      <th>Visits</th>
                      <th>Blocked</th>
                      <th>Off-task</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byDay.map((d) => (
                      <tr key={d.date}>
                        <td>{d.date}</td>
                        <td>{hm(d.activeMinutes)}</td>
                        <td>{d.visits}</td>
                        <td className={d.blocked ? 'warn-text' : ''}>{d.blocked}</td>
                        <td className={d.offTask ? 'warn-text' : ''}>{d.offTask}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted small">No activity in this range.</p>
            )}
          </section>

          {attendance && (
            <section className="card stack">
              <h2 className="with-icon">
                <CalendarCheck size={18} /> System Estimated Attendance
              </h2>
              {attendance.schoolDays ? (
                <>
                  <p className="muted small">
                    Present on <strong>{attendance.presentDays}</strong> of <strong>{attendance.schoolDays}</strong> school days (
                    {Math.round((attendance.presentDays / attendance.schoolDays) * 100)}%). Estimated by ClassGuardian, not official attendance: a day counts as present when the
                    child was active on their computer during school time.
                    {attendance.holidayDays > 0 && ` ${attendance.holidayDays} holiday/vacation day${attendance.holidayDays === 1 ? '' : 's'} in this range were not counted.`}
                  </p>
                  <ul className="attendance-list">
                    {[...attendance.days].reverse().map((d) => (
                      <li key={d.date} className={d.present ? 'present' : 'absent'}>
                        <span className={`attendance-badge ${d.present ? 'present' : 'absent'}`}>{d.present ? 'Present' : 'Absent'}</span>
                        <span className="grow">
                          {d.weekday} {d.date}
                        </span>
                        {d.present && d.firstSeen && d.lastSeen && (
                          <span className="muted small">
                            {clock(d.firstSeen)}–{clock(d.lastSeen)} · {d.visits} visit{d.visits === 1 ? '' : 's'}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="muted small">No school days in this range (set school hours in the child's Schedule).</p>
              )}
            </section>
          )}

          <p className="muted small center">
            Monitoring only records during school time. Website history is kept for {parent.historyDays ?? 90} days.
          </p>
        </>
      ) : null}
    </div>
  );
}
